import { get } from 'lodash';

import { PropTypes } from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import Analytics from 'analytics-opencare';

import { otherInsurance, topCanadianInsurance } from 'common/constants';
import { Form } from 'opencare-crown';

import { DIRECTION } from 'pages/common/components/Autocomplete/types';
import {
  DIMENSIONS,
  useWindowDimensions,
} from 'providers/WindowDimensionsProvider';
import scrollToRef from 'utils/scrollToRef';

import { selectDataInsuranceListForCanada } from '../../../../../../connect/selectors';
import InsurancePickerCanada from '../../../../../common/components/InsurancePickerCanada/InsurancePickerCanada';
import SingleSelect from '../../../../../common/components/SingleSelect/SingleSelect';
import {
  AdditionalBlock,
  OptionBlock,
  PrimaryButton,
  Subtitle,
  Title,
} from '../../../../../common/styled-components';
import { useFunnelStorage } from '../../../../storage/Storage';
import {
  ButtonBlock,
  SecondaryButton,
  StepWrapper,
} from '../../../../styled-components';
import { generateStepAnalytics, STEP_KEY } from '../../../../utils';
import { SUBSTEPS } from '../../constants';

import { featuredCanadianInsurance } from './constants';

const analytics = new Analytics();

const CanadianInsurance = ({ position, moveToNextStep, setSubStep }) => {
  const storage = useFunnelStorage();
  const refToOtherDropdown = useRef();
  const { layout } = useWindowDimensions();

  const [insuranceForm] = Form.useForm();

  const savedInsurance = storage.get(STEP_KEY.INSURANCE);

  const insuranceListForCanada =
    useSelector(selectDataInsuranceListForCanada) || [];

  const [showDropdown, setShowDropdown] = useState(
    get(savedInsurance, 'hasInsurance') === 'Yes'
      ? get(savedInsurance, 'topProvider') === false
      : false,
  );
  const [dropdownValue, setDropdownValue] = useState(
    get(savedInsurance, 'hasInsurance') === 'Yes'
    && get(savedInsurance, 'topProvider') === false
      ? get(savedInsurance, 'provider')
      : null,
  );

  const savedInsuranceProgress = storage.get(STEP_KEY.INSURANCE);
  const featuredInsurance = topCanadianInsurance.map((insurance) => {
    const logo = featuredCanadianInsurance[insurance.toLowerCase()];
    return {
      title: insurance,
      logo:
        logo.desktop && layout !== DIMENSIONS.MOBILE
          ? logo.desktop
          : logo.mobile,
    };
  });

  useEffect(() => {
    if (showDropdown) {
      scrollToRef(refToOtherDropdown);
    }
  }, [showDropdown]);

  const handleInsuranceSelection = (
    insurance,
    topProvider,
    askIfSocialAssistance,
  ) => {
    analytics.trackClick({
      pageType: analytics.pageTypes.FUNNEL,
      pageName: 'funnel_insurance-plan',
      name: 'wizard',
      section: 'insurance-plan',
      ...generateStepAnalytics(position, storage, {
        response: insurance,
      }),
    });

    const insuranceRecord = insuranceListForCanada.find(
      (insuranceItem) => insuranceItem.name === insurance,
    );

    storage.save(
      STEP_KEY.INSURANCE,
      {
        ...savedInsuranceProgress,
        hasInsurance: 'Yes',
        provider: insurance,
        providerId: get(insuranceRecord, 'id'),
        topProvider,
        socialAssistance: get(insuranceRecord, 'isSocialAssistance') === true,
      },
      !askIfSocialAssistance,
    );

    if (askIfSocialAssistance) {
      setSubStep(SUBSTEPS.HAS_SOCIAL_ASSISTANCE);
    } else {
      moveToNextStep();
    }
  };

  // `provider` is undefined when the user clears the dropdown input
  const handleDropdownChange = (provider) => {
    setDropdownValue(provider?.name ?? '');
  };

  const getInsurance = (selectedInsurance) =>
    insuranceListForCanada.find((insuranceItem) =>
      [insuranceItem.name, insuranceItem.id, insuranceItem].includes(
        selectedInsurance,
      ));

  const handleSubmit = () => {
    if (dropdownValue === otherInsurance.name) {
      handleInsuranceSelection('Other', false, true);
    } else {
      handleInsuranceSelection(getInsurance(dropdownValue)?.name, false);
    }
  };

  return (
    <div data-testid="canada">
      <Title id="title">Who is your dental insurance provider?</Title>
      <Subtitle>
        If you're not sure - don't worry - you can change your answer later.
      </Subtitle>
      <StepWrapper>
        <Form form={insuranceForm} onFinish={handleSubmit}>
          <OptionBlock row={4}>
            {featuredInsurance.map((insurance) => (
              <SingleSelect
                key={insurance.title}
                id={insurance.title}
                Logo={insurance.logo}
                title={insurance.title}
                describedBy="title"
                onChange={() => handleInsuranceSelection(insurance.title, true)}
                selected={get(savedInsurance, 'provider') === insurance.title
                && !showDropdown}
              />
            ))}

            <SingleSelect
              key="other-provider"
              id="other-provider"
              title="Other provider..."
              describedBy="title"
              onChange={() => setShowDropdown(!showDropdown)}
              selected={showDropdown || get(savedInsurance, 'topProvider') === false}
            />
          </OptionBlock>

          {showDropdown
          && (
            <AdditionalBlock
              data-testid="other-dropdown"
              ref={refToOtherDropdown}
              tabIndex="-1"
            >
              <InsurancePickerCanada
                form={insuranceForm}
                preferredDropDirection={layout === DIMENSIONS.MOBILE
                  ? DIRECTION.UP
                  : DIRECTION.DOWN}
                hidePopular
                showNotListed
                defaultProvider={dropdownValue}
                updateInsurance={handleDropdownChange}
              />
            </AdditionalBlock>
          )}
          <ButtonBlock>
            {!showDropdown
            && (
              <SecondaryButton
                id="unsure"
                type="button"
                describedBy="title"
                data-testid="option-unsure"
                onClick={() => handleInsuranceSelection('Unsure', false, true)}
              >
                I am unsure
              </SecondaryButton>
            )}

            {showDropdown
            && (
              <PrimaryButton
                type="submit"
                data-testid="submit"
                disabled={!dropdownValue}
              >
                Continue
              </PrimaryButton>
            )}
          </ButtonBlock>
        </Form>
      </StepWrapper>
    </div>
  );
};

CanadianInsurance.defaultProps = {
  position: '',
};

CanadianInsurance.propTypes = {
  position: PropTypes.string,
  moveToNextStep: PropTypes.func.isRequired,
  setSubStep: PropTypes.func.isRequired,
};

export default CanadianInsurance;
